/**
 * <s-sticky-atc> — mobile "Add to cart" bar for product pages.
 *
 * Markup contract:
 *   <s-sticky-atc data-form="[data-product-form]" inert class="... translate-y-full">
 *     <span data-sticky-price>…</span>
 *     <button type="button" data-sticky-add>Add to cart</button>
 *   </s-sticky-atc>
 *
 * The bar slides in once the main product form has scrolled above the viewport, and hides again when
 * the form is back in view or the footer is reached. Its button clicks the main form's add button, so
 * the selected variant, quantity and cart-drawer behaviour all stay with the real form. The price and
 * sold-out state are re-read from the page each time the bar shows, because variant changes swap them.
 */
class SStickyAtc extends HTMLElement {
  #formVisible = true;
  #formAbove = false;
  #footerVisible = false;
  #observers = [];

  connectedCallback() {
    const form = document.querySelector(this.dataset.form || '[data-product-form]');
    if (!form) return;

    const formObserver = new IntersectionObserver(([entry]) => {
      this.#formVisible = entry.isIntersecting;
      this.#formAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.#update();
    });
    formObserver.observe(form);
    this.#observers.push(formObserver);

    const footer = document.querySelector('footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        this.#footerVisible = entry.isIntersecting;
        this.#update();
      });
      footerObserver.observe(footer);
      this.#observers.push(footerObserver);
    }

    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    this.removeEventListener('click', this.#onClick);
  }

  get #mainAddButton() {
    const form = document.querySelector(this.dataset.form || '[data-product-form]');
    return form?.querySelector('button[name="add"]:not([disabled]):not([aria-disabled="true"])') ?? null;
  }

  #update() {
    const show = this.#formAbove && !this.#formVisible && !this.#footerVisible;
    if (show) this.#sync();
    this.classList.toggle('translate-y-full', !show);
    this.inert = !show;
  }

  #sync() {
    const target = this.querySelector('[data-sticky-price]');
    const current = document.querySelector('[data-product-price] > div > span');
    if (target && current) target.replaceChildren(current.cloneNode(true));

    const button = this.querySelector('[data-sticky-add]');
    if (!button) return;
    const available = Boolean(this.#mainAddButton);
    button.disabled = !available;
    button.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    button.dataset.label ??= button.getAttribute('aria-label') ?? '';
    if (available && button.dataset.label) button.setAttribute('aria-label', button.dataset.label);
    else button.removeAttribute('aria-label');
  }

  #onClick = (event) => {
    const button = event.target.closest('[data-sticky-add]');
    if (!button || button.disabled) return;
    this.#mainAddButton?.click();
  };
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
